import { Link } from 'react-router-dom';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { bandValueOf } from '../../domain/plants/plantFilters';
import { bandSrcSet } from '../../domain/plants/plantMedia';
import { HeartIcon } from '../chrome/BrandIcons';
import { BAND_ICONS, PROPERTY_ICONS } from './PlantIcons';
import WaterMeter from './WaterMeter';

/**
 * One plant on a shelf (FR-05), composed to `catalog-netflix.png`: the artwork,
 * then the name, then a row of glyphs for the three properties a planting
 * decision turns on.
 *
 * The artwork carries nothing over it but the save control. SPEC-012 D-3 put a
 * scale figure and a height range in its bottom-right corner, over a gradient of
 * the card's wash; SPEC-012 PR-12.9 took both away. The gradient was shading laid
 * across the foot of every illustration, which is the one thing the specimens had
 * just been re-printed to be rid of (SPEC-002 PR-2.15), and the figure it existed
 * to back was standing in the plant. The height is on the detail page, and the
 * size bands still filter the shelf.
 *
 * The card's background is the plant's own wash, sampled from its illustration
 * at ingest (SPEC-002 D-7) — so two perennials sit on different colours, as the
 * mockups have it. The component never authors a colour: it names a token and
 * lets the sheet resolve it, which is what keeps `tokens.css` the only file with
 * a hex in it.
 *
 * SPEC-011 printed a five-column plate of icons AND text here, reading NFR-04's
 * "text labels alongside critical pictograms" as a rule about the pixels. On a
 * 163px shelf card that plate was five stacks of hyphenated 9px words, and it is
 * what the approved design replaces. SPEC-012 D-2 spends the text budget
 * differently: the VALUE is drawn — three droplets with one filled, a cloud
 * against a sun — and the words are still there for a screen reader, in a
 * `.visually-hidden` label per glyph. The fact never has one carrier.
 */

/**
 * The row, in the reference's order. Size is not in it — the card answers what a
 * plant is LIKE, and how big it gets is a number the detail page prints.
 */
const GLYPH_PROPERTIES = ['water', 'sun', 'soil'];

export default function PlantCard({ plant, isSaved = false, onToggleSave = null }) {
    const { t, localeCode } = useLocale();
    const { illustrationBase, illustrationWidths } = plant.media;
    const commonName = plant.prose?.commonName ?? plant.id;

    return (
        <article className="plant-card" style={{ '--plant-wash': `var(--wash-${plant.wash})` }}>
            <Link className="plant-card__link" to={`/${localeCode}/plants/${plant.id}`}>
                <div className="plant-card__stage">
                    <img
                        className="plant-card__art"
                        src={`${illustrationBase}-${illustrationWidths[0]}.webp`}
                        // A card is a thumbnail, so it offers only the rungs a
                        // cropped rendering will pay for — the ingest's
                        // native-width tier belongs to the viewer alone.
                        srcSet={bandSrcSet(illustrationBase, illustrationWidths)}
                        // Roughly two cards across the shelf's visible pair.
                        sizes="(min-width: 640px) 300px, 45vw"
                        // The illustration repeats what the heading already says, so
                        // announcing it twice would only slow a screen reader down.
                        alt=""
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="500"
                    />
                </div>
                <div className="plant-card__head">
                    <h3 className="plant-card__name">{commonName}</h3>
                    <p className="plant-card__botanical">{plant.botanicalName}</p>
                </div>
            </Link>
            {/* Outside the link, because a button inside one is a link you cannot
                press without following it. Absolutely placed over the artwork's
                corner, at the 44px minimum (SPEC-011 D-6). */}
            {onToggleSave ? (
                <button
                    type="button"
                    className="plant-card__save"
                    onClick={() => onToggleSave(plant.id)}
                    aria-pressed={isSaved}
                    aria-label={t(isSaved ? 'plants.card.unsave' : 'plants.card.save', { plant: commonName })}
                >
                    <HeartIcon size={20} filled={isSaved} />
                </button>
            ) : null}
            <dl className="plant-card__facts">
                {GLYPH_PROPERTIES.map((key) => {
                    const detail = plant.properties[key];
                    // A plant scored outside the bands still gets its property's
                    // own glyph. An ingest that invents a fourth soil score is a
                    // content bug to fix, not a card to blank out.
                    const BandIcon = BAND_ICONS[key]?.[bandValueOf(plant, key)] ?? PROPERTY_ICONS[key];

                    return (
                        <div className={`plant-card__fact plant-card__fact--${key}`} key={key}>
                            {/* The pair is one fact, so it is announced as one
                                phrase — "Water: Low" — rather than as a term and a
                                definition a reader has to join up itself. */}
                            <dt className="visually-hidden">{t(`plants.property.${key}`)}</dt>
                            <dd>
                                {key === 'water'
                                    ? <WaterMeter score={detail.score} />
                                    : <BandIcon size={18} />}
                                <span className="visually-hidden">{t(`${detail.labelKey}.short`)}</span>
                            </dd>
                        </div>
                    );
                })}
            </dl>
        </article>
    );
}
